import * as React from "react";
import { FC, useEffect, useRef } from "react";
import { Feature, Map } from "ol";
import { Layer } from "ol/layer";
import { useMap } from "./useMap";
import { IGameUserPosition, useGameLocation } from "./Game/useGameLocation";
import { FeatureType, RouteLayers, SelectedStatus } from "./enums";
import { GeoLocation } from "./GeoLocation";
import { FilterOptions } from "./filters";
import Portal from "./Portal";
import "../css/icons/style.css";
import "../css/map.css";
import "ol/ol.css";

export type OnFeatureClickCallback = (feature: Feature) => void;
export type OnUnselectCallback = (feature: Feature) => void;
export type OnRouteClickCallback = (feature: Feature) => void;
export type OnRouteUnselectCallback = (feature: Feature) => void;
export type OnGetRouteFeaturesCallback = (layerFeatures: ILayerFeatures[]) => void;
export type OnGameLocationChangeCallback = (position: IGameUserPosition) => void;
export type OnGameSelectedCallback = (feature: Feature) => void;
export type OnGameCompletedCallback = (feature: Feature) => void;
export type OnCheckPointSelectedCallback = (feature: Feature) => void;

export interface IMammadabaMapProps {
    /**
     * (Required) All of the features, with custom data fields added, such as "VISITED", "LIKE" etc
     */
    allFeatures: Feature[] | null;
    /**
     * (optional) array of filtered objects (features), visible on map
     * @default [] means, no objects (features) visible on map, only basemap is visible
     */
    features: Feature[] | null;
    /**
     * array of layers, containing predefined Route data
     */
    routeLayers: Layer[];
    /**
     * (optional) array of filtered Routes (features), visible on map
     * @default null means, all Routes (features) visible on map
     */
    filteredRoutes: Feature[] | null;
    /**
     * array of layers, containing predefined Games data
     */
    gameLayers: Layer[];
    /**
     * callback on object click, returns identified object (feature)
     */
    onClickOnFeature: OnFeatureClickCallback;
    /**
     * callback, when object unselected, returns OBJECTID of unselected feature
     */
    onUnselect: OnUnselectCallback;
    /**
     * set, to select Feature programmmaticaly, by its OBJECTID and layer id
     * must contain { layerId: string; objectId: string }.
     * Use this to select Map feature from MiniMap click, as Minimap features is only clones of original features
     */
    selectFeatureById: { layerId: string; objectId: string };
    /**
     * set, to select Route programmmaticaly
     * must contain { routeId: string, timestamp: number }. Timestamp must be different every time
     * set 'routeId: undefined', to unselect route
     */
    selectRouteById: { routeId: string; timestamp: number };
    /**
     * set, to select Game programmmaticaly
     * must contain { gameId: string, timestamp: number }. Timestamp must be different every time
     * set 'gameId: undefined', to unselect game
     */
    selectGameById: { gameId: string; timestamp: number };
    /**
     * set, to select Game CheckPoint programmmaticaly, use POINTID field value
     * must contain { checkPointId: string, timestamp: number }. Timestamp must be different every time
     * set 'checkPointId: undefined', to unselect any checkpoints
     */
    selectGameCheckPointById?: { checkPointId: string; timestamp: number };
    /**
     * callback, when Game selected
     * @returns selected Game main point feature
     */
    onGameSelected: OnGameSelectedCallback;
    /**
     * start the game, pass Game main point feature
     */
    startGame: Feature;
    /**
     * pass Game main point feature, to finish the game, when all tasks solved
     */
    gameCompleted: Feature;
    /**
     * callback, when Route selected
     * @returns selected Route feature
     */
    onRouteSelect: OnRouteClickCallback;
    /**
     * callback, when Route unselected
     * @returns ROUTEID value of unselected Route feature
     */
    onRouteUnselect: OnRouteUnselectCallback;
    /**
     * boolean flag, sets if map visible/hidden
     */
    mapVisible: boolean;
    /**
     * array of objects, to change layer visibility
     * @example [{ layerId: "routeTrackLayer", visibility: false }]
     */
    layerVisibility: IVisibleLayer[];
    /**
     * feature, to zoom-to
     */
    zoomTo: Feature | null;
    /**
     * boolean flag, show or hide circle with nearby filter radius
     */
    nearbyCircleVisible: boolean;
    /**
     * map padding, left, in px, animation duration, in ms
     * NOTE: for map transitions, easing function is "ease-in-out", so it is reccomended to use it for sidebar too
     * @example { left: 300, duration: 500 }
     */
    padding: IPadding;
    /**
     * callback, returns game user position
     * @returns IGameUserPosition - coordinates and other data if needed
     */
    onGameLocationChange: OnGameLocationChangeCallback;
    /**
     * callback, fires, when game completed (all game checkpoints visited)
     * @returns main feature of completed game
     */
    onGameCompleted: OnGameCompletedCallback;
    /**
     * for game, optional parameter. Object approaching radius
     */
    gameApproachingRadius?: number;
    /**
     * for game, optional parameter. Object leaving radius
     */
    gameLeavingRadius?: number;
    /**
     * Game check point feature, marked as Found
     */
    checkPointFound: Feature;
    /**
     * callback, when Game checkpoint selected by map click
     */
    onCheckPointSelected: OnCheckPointSelectedCallback;
    /**
     * pass true, to use simulator instead of real user location
     */
    useGameSimulator?: boolean;
    /**
     * filter options, to use for filters
     */
    filterOptions: FilterOptions;
}

export interface IVisibleLayer {
    layerId: RouteLayers | FeatureType | string;
    visibility: boolean;
}

export interface IUpdates {
    selectedState?: SelectedStatus;
    visited?: number;
    like?: number;
    route?: number;
}

export interface IPadding {
    left: number;
    duration: number;
}

export interface ILayerFeatures {
    layerId: string;
    features: Feature[];
}

let mapRef: React.MutableRefObject<Map>;

export const MammadabaMap: FC<IMammadabaMapProps> = (props) => {
    const ref = useRef<HTMLDivElement>();

    mapRef = useRef<Map>();
    mapRef.current = useMap(ref, props);
    const gameLocation = useGameLocation(mapRef.current, props);
    // @ts-ignore
    window.allFeatures = props.allFeatures;

    const [locationButton, setLocationButton] = React.useState<boolean>(false);

    const style = props.mapVisible ? { height: "100%", display: "block" } : { display: "none" };

    useEffect(() => document.getElementById("location-button") && setLocationButton(true), []);

    return (
        <>
            <div ref={ref} style={style} />
            {locationButton ? (
                <Portal id={"location-button"}>
                    <GeoLocation map={mapRef.current} />
                </Portal>
            ) : null}
        </>
    );
};

export const updateAttributes = (feature: Feature, updates: IUpdates, allFeatures: Feature[]): void => {
    if (updates) {
        updates.visited !== undefined && feature.set("VISITED", updates.visited);
        updates.like !== undefined && feature.set("LIKE", updates.like);
        updates.route !== undefined && feature.set("ROUTE", updates.route);

        if (updates.selectedState === SelectedStatus.Default || updates.selectedState === SelectedStatus.Selected) {
            for (const feature of allFeatures) {
                if (feature.get("SELECTED") !== SelectedStatus.Default) {
                    feature.set("SELECTED", SelectedStatus.Default);
                }
            }
            feature.values_.SELECTED = updates.selectedState;
        }
    }
    feature.changed();
};

export const getMap = (): Map => mapRef.current;
